import {
  BarChart,
  Bell,
  BookOpen,
  FileText,
  HelpCircle,
  Home,
  LayoutGrid,
  Megaphone,
  Palette,
  Pencil,
  Settings,
  Star,
  Users,
} from 'lucide-react';
import { useEmbeddedAuth } from '@src/contexts/embedded-auth/useEmbeddedAuth';
import { Button } from '@repo/ui/components/Button';
import { Card, CardContent } from '@repo/ui/components/Card';
import { cn } from '@repo/ui/utils';
import { useGetUser } from '@src/api/useUser';
import { useHasPermission } from '@src/hooks/useHasPermission';
import { useIsMobile } from '@src/hooks/useIsMobile';
import { useIsNarrowScreen } from '@src/hooks/useIsNarrowScreen';
import { useUniversityPublicWebsiteUrl } from '@src/hooks/useUniversityPublicWebsiteUrl';
import { useContext, useEffect } from 'react';
import { MenuContext } from '../../contexts/MenuContext';
import NavButton from './NavButton';

const Sidebar = () => {
  const { isAuthenticated } = useEmbeddedAuth();
  const { data: user } = useGetUser(isAuthenticated);
  const { menu, setMenu } = useContext(MenuContext);
  const { hasPermission } = useHasPermission();
  const { publicWebsiteUrl } = useUniversityPublicWebsiteUrl();
  const isMobile = useIsMobile();
  const isNarrowScreen = useIsNarrowScreen();

  useEffect(() => {
    setMenu(!isNarrowScreen);
  }, [isNarrowScreen, setMenu]);

  return (
    <>
      {isMobile && menu && (
        <div
          className="fixed inset-0 z-40 bg-black/40"
          onClick={() => setMenu(false)}
          aria-hidden="true"
        />
      )}
      <div
        className={cn(
          'bottom-0',
          isNarrowScreen && menu
            ? 'fixed left-0 z-50 top-[42px] md:top-[64px]'
            : 'absolute top-[42px] md:top-[64px]'
        )}
      >
        <Card
          id="nav-wrapper"
          hidden={!menu}
          className={cn(
            'shadow-transparent',
            'border-0',
            'peer',
            'h-full',
            'bg-primary',
            'text-primary-foreground'
          )}
          role="navigation"
          aria-label="Main"
        >
          <CardContent
            className={cn(
              'px-0',
              'flex',
              'flex-col',
              'justify-between',
              'h-full',
              'pb-0'
            )}
          >
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              className={cn('py-2', 'overflow-y-auto')}
              onClick={() => isNarrowScreen && setMenu(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && isNarrowScreen) setMenu(false);
              }}
            >
              <NavButton to="/" icon={<Home className={cn('h-4', 'w-4')} />}>
                Home
              </NavButton>
              <NavButton
                to="/publication-overview"
                icon={<FileText className={cn('h-4', 'w-4')} />}
                hasPermission={hasPermission('publish') || user?.isAuthoring}
              >
                Publication Overview
              </NavButton>
              <NavButton
                to="/authoring"
                icon={<Pencil className={cn('h-4', 'w-4')} />}
                hasPermission={hasPermission('publish')}
              >
                Authoring
              </NavButton>
              <NavButton
                to="/reviews"
                icon={<Star className={cn('h-4', 'w-4')} />}
                hasPermission={hasPermission('review') || user?.hasReviews}
              >
                Reviews
              </NavButton>
              <NavButton
                to="/messages"
                icon={<Bell className={cn('h-4', 'w-4')} />}
              >
                Messages
              </NavButton>
              <NavButton
                to="/manage-people"
                icon={<Users className={cn('h-4', 'w-4')} />}
                hasPermission={hasPermission('access-manage-affiliations-page')}
              >
                Manage People
              </NavButton>
              <NavButton
                to="/manage-departments"
                icon={<LayoutGrid className={cn('h-4', 'w-4')} />}
                hasPermission={hasPermission('access-manage-departments-page')}
              >
                Manage Departments
              </NavButton>
              <NavButton
                to="/branding"
                icon={<Palette className={cn('h-4', 'w-4')} />}
                hasPermission={hasPermission('access-manage-branding-page')}
              >
                Manage Branding
              </NavButton>
              <NavButton
                to="/statistics"
                icon={<BarChart className={cn('h-4', 'w-4')} />}
                hasPermission={hasPermission('read-university-statistics')}
              >
                Statistics
              </NavButton>
              <Button
                variant="ghost"
                className={cn(
                  'w-full',
                  'justify-start',
                  'nav-item',
                  'hover:bg-primary-hover',
                  'hover:text-primary-foreground'
                )}
                asChild
              >
                <a
                  href={publicWebsiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <BookOpen className={cn('mr-2', 'h-4', 'w-4')} />
                  Reading
                </a>
              </Button>
              <Button
                variant="ghost"
                className={cn(
                  'w-full',
                  'justify-start',
                  'nav-item',
                  'mb-2',
                  'hover:bg-primary-hover',
                  'hover:text-primary-foreground'
                )}
                asChild
              >
                <a
                  href="https://help.dynamica.scifree.se/hc/en-gb"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <HelpCircle className={cn('mr-2', 'h-4', 'w-4')} />
                  Help
                </a>
              </Button>
              <NavButton
                to="/admin-settings"
                icon={<Settings className={cn('h-4', 'w-4')} />}
                hasPermission={hasPermission('update-manuscript-doi')}
              >
                Admin Settings
              </NavButton>
              <NavButton
                to="/announcements"
                icon={<Megaphone className={cn('h-4', 'w-4')} />}
                hasPermission={hasPermission('manage-announcements')}
              >
                What&apos;s New
              </NavButton>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default Sidebar;
